import React, { useState, useEffect, useCallback } from "react";
import { getRequest, postRequest } from "../../utils/httpRequest";
import { colors } from "../AgenticPaymentAirdrop/styles";
import {
  card, note, help, th, thNum, td, tdNum, mono, tableBox, grid, btn, ghostBtn,
  TONES, Tag, Banner, num, amount, when, sentence, signed, signedTone, Field, hashscanTx,
} from "../PoolProtection/shared";

// Minting pool profit as USDb to the Fees wallet, moved off Pool Protection so
// that page holds only pool protection. Profit here is trading only: the
// opening and corrections from putting pools at 100% are never minted. lp-api
// checks every mint against the USDC reserve, and the signing server checks it
// again. Managers see the figures; only a super admin can mint. The calls go
// through Pool Protection's page actions.

const RUN_STATUS = {
  pending: { tone: "info", text: "Pending" },
  minted: { tone: "good", text: "Minted" },
  failed: { tone: "bad", text: "Failed" },
  needs_review: { tone: "bad", text: "Needs review" },
};

// lp-api's messages come without a closing period.
const fullStop = (t) => (/[.!?]$/.test(String(t).trim()) ? String(t).trim() : `${String(t).trim()}.`);

// lp-api's reasons carry raw numbers ("7262.861234"); they read like the
// figures on the card ("7,262.86"). Ids such as 0.0.123 are left alone.
export const withAmounts = (t) =>
  String(t).replace(/(?<![\d.])\d+(?:\.\d+)?(?![\d.])/g, (n) => (n.includes(".") || n.length > 3 ? amount(n) : n));

// What Mint now can take right now, or null against an lp-api that doesn't
// report it yet.
export const mintableOf = (d) => (d?.mintableUsdb == null ? null : num(d.mintableUsdb));

// The line people read first on the Profit mint card, as { tone, text }, or
// null when lp-api doesn't report what can be minted.
export const mintStatus = (d) => {
  if (!d) return null;
  if (d.reserveError) return { tone: "bad", text: `The reserve couldn't be read: ${fullStop(d.reserveError)}` };
  const mintable = mintableOf(d);
  if (mintable == null) return null;
  if (d.blockedReason) return { tone: "warn", text: `Can't mint. ${fullStop(withAmounts(d.blockedReason))}` };
  return { tone: "good", text: `Up to ${amount(mintable)} USDb can be minted.` };
};

const CHAINS = { hedera: "Hedera", polygon: "Polygon", stellar: "Stellar" };
// "Hedera 349.12 / Polygon 1,928.88 / Stellar 0.00"
export const byChainLine = (byChain) =>
  (byChain || []).map((c) => `${CHAINS[c.chain] || sentence(c.chain)} ${amount(c.usdc)}`).join(" / ");

// What a "Mint now" answer means, as { tone, text }.
export const mintResult = (res) => {
  const run = res?.run;
  if (run?.status === "minted") return { tone: "good", text: `Minted ${amount(run.amountUsdb)} USDb to the Fees wallet.` };
  if (run?.status === "needs_review") {
    return { tone: "bad", text: `Minted ${amount(run.amountUsdb)} USDb, but the transfer to the Fees wallet failed.${run.error ? ` ${sentence(run.error)}` : ""}` };
  }
  if (run) return { tone: "bad", text: `Mint ${RUN_STATUS[run.status]?.text.toLowerCase() || run.status}.${run.error ? ` ${sentence(run.error)}` : ""}` };
  return { tone: "bad", text: `Not minted. ${sentence(res?.reason || "lp-api gave no reason")}.` };
};



const Stat = ({ title, value, tone, hint }) => (
  <div>
    <div style={{ fontSize: "12px", color: colors.gray600 }}>{title}</div>
    <div style={{ fontSize: "18px", fontWeight: 700, fontVariantNumeric: "tabular-nums", color: tone === "bad" ? colors.danger : undefined }}>{value}</div>
    {hint && <div style={help}>{hint}</div>}
  </div>
);


const ProfitMint = ({ profitMintUrl, runProfitMintUrl, hederaNetwork, canRun = false }) => {
  const [mint, setMint] = useState(null); // { data } or { error }
  const [loading, setLoading] = useState(true);
  const [mintAmount, setMintAmount] = useState(null); // null while the mint form is closed
  const [mintMessage, setMintMessage] = useState(null); // { tone, text }, shown in the card
  const [busy, setBusy] = useState(false);

  // Reading the reserve can take a while.
  const loadMint = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getRequest(profitMintUrl);
      setMint(res?.success ? { data: res.data } : { error: res?.error || "Couldn't load the profit mint." });
    } catch (e) {
      setMint({ error: "Couldn't load the profit mint." });
    } finally {
      setLoading(false);
    }
  }, [profitMintUrl]);

  useEffect(() => { loadMint(); }, [loadMint]);

  // Every submit ends with a line in the card: the new run, or why nothing was minted.
  const runMint = async () => {
    const limit = mintableOf(mint?.data) ?? num(mint?.data?.unmintedUsdb);
    const given = String(mintAmount || "").trim();
    if (given && !(Number(given) > 0)) return setMintMessage({ tone: "bad", text: "Enter an amount above 0, or leave it empty." });
    if (given && Number(given) > limit) return setMintMessage({ tone: "bad", text: `Enter up to ${amount(limit)} USDb.` });
    setBusy(true);
    setMintMessage(null);
    try {
      const res = await postRequest(runProfitMintUrl, given ? { amountUsdb: Number(given) } : {});
      if (!res?.success) return setMintMessage({ tone: "bad", text: fullStop(res?.error || "Not minted. lp-api gave no reason") });
      setMintMessage(mintResult(res.data));
      setMintAmount(null);
      await loadMint();
    } catch (e) {
      setMintMessage({ tone: "bad", text: fullStop(e?.data?.error || "Not minted. lp-api didn't answer") });
    } finally {
      setBusy(false);
    }
  };

  const mintData = mint?.data;
  const reserve = mintData?.reserve;
  const runs = mintData?.runs || [];
  const mintable = mintableOf(mintData);
  const canMint = mintable == null || mintable >= 1;
  const statusLine = mintStatus(mintData);

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: "20px" }}>
        <button type="button" style={ghostBtn(loading)} disabled={loading} onClick={loadMint}>{loading ? "Refreshing…" : "Refresh"}</button>
      </div>
      <div style={card}>
        <p style={note}>
          Mint pool profit as USDb to the Fees wallet. Minting only runs when the USDC reserve covers it.{" "}
          {mintData && (mintData.enabled ? <Tag tone="good">Daily mint on</Tag> : <Tag tone="info">Daily mint off</Tag>)}
        </p>
        {!mint ? <p style={note}>Loading…</p>
          : mint.error ? <Banner tone="bad">{mint.error}</Banner> : (
          <>
            {statusLine && <Banner tone={statusLine.tone}>{statusLine.text}</Banner>}
            <div style={grid}>
              <Stat title="Not minted yet (USDb)" value={amount(mintData.unmintedUsdb)} tone={num(mintData.unmintedUsdb) < 0 ? "bad" : null} />
              <Stat title="Reserve headroom (USDb)" value={reserve ? signed(reserve.headroomUsdb) : "-"} tone={num(reserve?.headroomUsdb) < 0 ? "bad" : null} />
              <Stat title="Minted so far (USDb)" value={amount(mintData.mintedUsdb)} />
            </div>
            <details style={{ marginBottom: "16px", fontSize: "13px" }}>
              <summary style={{ cursor: "pointer", fontWeight: 600 }}>Reserve details</summary>
              <dl style={{ display: "grid", gridTemplateColumns: "max-content 1fr", gap: "6px 16px", margin: "12px 0 0" }}>
                <dt style={{ color: colors.gray600 }}>USDC reserve</dt>
                <dd style={{ margin: 0 }}>
                  {reserve ? amount(reserve.reserveUsdc) : "-"}
                  {(reserve?.byChain || []).length > 0 && <div style={help}>{byChainLine(reserve.byChain)}</div>}
                </dd>
                <dt style={{ color: colors.gray600 }}>USDb in circulation</dt>
                <dd style={{ margin: 0 }}>{reserve ? amount(reserve.usdbSupply) : "-"}</dd>
                <dt style={{ color: colors.gray600 }}>Owed by pools (USDb)</dt>
                <dd style={{ margin: 0 }}>{amount(mintData.poolsOwedUsdb)}</dd>
                <dt style={{ color: colors.gray600 }}>Net profit (USDb)</dt>
                <dd style={{ margin: 0, ...signedTone(mintData.profitNetUsdb) }}>{signed(mintData.profitNetUsdb)}</dd>
                {(reserve?.reserveTokenIds || []).length > 0 && (
                  <>
                    <dt style={{ color: colors.gray600 }}>Reserve token</dt>
                    <dd style={{ margin: 0, ...mono }}>{reserve.reserveTokenIds.join(", ")}</dd>
                  </>
                )}
                <dt style={{ color: colors.gray600 }}>Daily mint</dt>
                <dd style={{ margin: 0 }}>
                  {mintData.enabled ? `On, at 04:00 UTC, up to ${amount(mintData.autoMaxUsdb)} USDb a day` : "Off"}
                </dd>
              </dl>
            </details>
            {canRun && mintAmount == null && (
              <div style={{ marginBottom: "16px" }}>
                <button type="button" style={btn(busy || !canMint)} disabled={busy || !canMint}
                  onClick={() => { setMintAmount(""); setMintMessage(null); }}>Mint now</button>
              </div>
            )}
            {canRun && mintAmount != null && (
              <div style={{ marginBottom: "16px", padding: "16px", border: `1px solid ${colors.border}`, borderRadius: "8px", background: colors.gray100 }}>
                <p style={{ margin: "0 0 12px", fontSize: "14px" }}>Mint pool profit to the Fees wallet?</p>
                <div style={{ ...grid, marginBottom: "12px" }}>
                  <Field id="mint-amount" title="Amount" suffix="USDb" type="number" min="0" step="any"
                    max={mintable == null ? undefined : mintable}
                    placeholder={`Up to ${amount(mintable ?? mintData.unmintedUsdb)}`}
                    hint={`Empty mints ${amount(mintable ?? mintData.unmintedUsdb)} USDb.`}
                    value={mintAmount} onChange={(e) => setMintAmount(e.target.value)} />
                </div>
                <button type="button" style={btn(busy)} disabled={busy} onClick={runMint}>{busy ? "Minting…" : "Mint"}</button>
                <button type="button" style={{ ...ghostBtn(busy), marginLeft: "8px" }} disabled={busy}
                  onClick={() => { setMintAmount(null); setMintMessage(null); }}>Cancel</button>
              </div>
            )}
            {mintMessage && <Banner tone={mintMessage.tone}>{mintMessage.text}</Banner>}
            <h3 style={{ fontSize: "14px", margin: "0 0 8px" }}>Recent mints</h3>
            {runs.length === 0 ? <p style={{ ...note, margin: 0 }}>No mints yet.</p> : (
              <div style={tableBox("50vh")}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr><th style={th}>Date</th><th style={thNum}>Amount (USDb)</th><th style={th}>Status</th><th style={th}>By</th><th style={th}>Transaction</th></tr>
                  </thead>
                  <tbody>
                    {runs.map((run) => {
                      const st = RUN_STATUS[run.status] || { tone: "info", text: sentence(run.status) };
                      return (
                        <tr key={run.runId} style={run.status === "needs_review" ? { background: TONES.bad.bg } : undefined}>
                          <td style={{ ...td, whiteSpace: "nowrap" }}>{when(run.createdAt)}</td>
                          <td style={tdNum}>{amount(run.amountUsdb)}</td>
                          <td style={td}>
                            <Tag tone={st.tone}>{st.text}</Tag>
                            {run.error && <div style={help}>{run.error}</div>}
                          </td>
                          <td style={td}>{run.actor || "-"}</td>
                          <td style={td}>
                            {run.mintTransactionId && (
                              <div><a href={hashscanTx(hederaNetwork, run.mintTransactionId)} target="_blank" rel="noopener noreferrer">Mint</a></div>
                            )}
                            {run.transferTransactionId && (
                              <div><a href={hashscanTx(hederaNetwork, run.transferTransactionId)} target="_blank" rel="noopener noreferrer">Transfer</a></div>
                            )}
                            {!run.mintTransactionId && !run.transferTransactionId && "-"}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ProfitMint;
